import { useParams, Link } from 'react-router-dom';
import {
  useGetMyInvestmentQuery,
  useListMyInvestmentPaymentsQuery,
  useListMyInvestmentPayoutsQuery,
  useListMyInvestmentRolloversQuery,
} from '../../features/investments/investorInvestmentsApi';
import { usePlanLookup } from '../../components/investments/usePlanLookup';
import InvestmentStatusBadge from '../../components/investments/InvestmentStatusBadge';
import FundingProgressBar from '../../components/investments/FundingProgressBar';
import PayoutInterface from '../../components/investments/PayoutInterface';
import PayoutTimeline from '../../components/investments/PayoutTimeline';
import RolloverStatusCard from '../../components/investments/RolloverStatusCard';
import RolloverHistory from '../../components/investments/RolloverHistory';
import PlanPhoto from '../../components/plans/PlanPhoto';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import { DataRow } from '../../components/ui/MobileCard';
import { SkeletonDetailCard, SkeletonTable } from '../../components/ui/Skeleton';
import { formatCurrency, formatDuration, formatRoi } from '../../lib/formatPlan';

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

// paid_at is a date, not a datetime — a separate formatter avoids showing a
// misleading 00:00:00 time component that was never actually in the data.
function formatDateOnly(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * GET /investor/investments/{investment} — a 404 here means either "this
 * investment doesn't exist" or "it belongs to someone else." Both render
 * identically as a generic "not found" — this screen never tries to tell
 * them apart (there's nothing useful to say differently either way). Same
 * treatment for the payments sub-resource. Read-only throughout: no
 * record-payment or record-payout action exists on the investor side.
 * Payouts (money returned to the investor) are shown read-only below
 * payments — a distinct concept/table from payments, see
 * normalizePayout's comment. Recording a payout is admin-only.
 *
 * v1.16.0: the Payout Interface summary section and history timeline are
 * shared with the admin detail screen (PayoutInterface/PayoutTimeline) —
 * this side just omits the onRecordPayout prop, which hides the action.
 * Every summary figure comes straight from the backend's PayoutSummary,
 * never recomputed here.
 */
export default function InvestmentDetail() {
  const { id } = useParams();
  const { data: investment, isLoading, isError } = useGetMyInvestmentQuery(id, { skip: !id });
  const { data: paymentsData, isLoading: isLoadingPayments } = useListMyInvestmentPaymentsQuery(
    { investmentId: id },
    { skip: !id }
  );
  const { data: payoutsData, isLoading: isLoadingPayouts } = useListMyInvestmentPayoutsQuery(
    { investmentId: id },
    { skip: !id }
  );
  const { data: rolloversData, isLoading: isLoadingRollovers } = useListMyInvestmentRolloversQuery(
    { investmentId: id },
    { skip: !id }
  );
  const getPlan = usePlanLookup();

  const payments = paymentsData?.payments ?? [];
  const payouts = payoutsData?.payouts ?? [];
  const rollovers = rolloversData?.rollovers ?? [];
  const summary = payoutsData?.summary ?? investment?.payoutSummary ?? null;
  const plan = investment ? getPlan(investment.investmentPlanUuid) : null;

  return (
    <div className="space-y-6">
      <Link to="/my-investments" className="text-sm text-accent hover:underline">
        ← Back to my investments
      </Link>

      {isLoading && <SkeletonDetailCard fields={6} />}

      {isError && (
        <Card>
          <p className="py-6 text-center text-danger">Investment not found.</p>
        </Card>
      )}

      {investment && (
        <>
          {investment.closedAt && (
            <div className="rounded-xl border border-border-strong bg-background-elevated px-4 py-3">
              <p className="text-sm font-medium text-foreground">
                Closed on {formatDateOnly(investment.closedAt)}
                {investment.closedBy?.fullName ? ` by ${investment.closedBy.fullName}` : ''}
                {investment.closeReason ? ` — ${investment.closeReason}` : ''}
              </p>
            </div>
          )}

          <Card className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-lg font-semibold text-foreground">{plan?.name || 'Investment'}</h1>
                <p className="text-sm text-foreground-muted">Created {formatDate(investment.createdAt)}</p>
              </div>
              <div className="flex gap-2">
                {investment.isOverride && <Badge tone="warning">Overridden</Badge>}
                <InvestmentStatusBadge status={investment.status} />
              </div>
            </div>

            {plan?.photo_url && <PlanPhoto src={plan.photo_url} alt={plan.name} className="h-40 w-full rounded-xl" />}

            <FundingProgressBar fundedMinor={investment.fundedAmountMinor} principalMinor={investment.principalMinor} />

            <div className="grid grid-cols-2 gap-3 text-sm">
              <DataRow label="Principal" value={formatCurrency(investment.principal)} />
              <DataRow label="ROI" value={formatRoi(investment.roiPercentage)} />
              <DataRow label="Duration" value={formatDuration(investment.durationInDays)} />
              <DataRow label="Funded" value={formatCurrency(investment.fundedAmount)} />
              <DataRow label="Remaining" value={formatCurrency(investment.remainingAmount)} />
              <DataRow label="Payout frequency" value={investment.payoutSummary?.frequencyLabel || '—'} />
              <DataRow label="Next payout due" value={investment.nextPayoutDueAt ? formatDateOnly(investment.nextPayoutDueAt) : '—'} />
              {investment.startDate && <DataRow label="Start date" value={formatDate(investment.startDate)} />}
              {investment.maturityDate && <DataRow label="Maturity date" value={formatDate(investment.maturityDate)} />}
              {investment.rolloverTermDays > 0 && (
                <DataRow label="Rollover term" value={formatDuration(investment.rolloverTermDays)} />
              )}
              {investment.rolloverCount > 0 && (
                <>
                  <DataRow label="Current term started" value={investment.currentTermStartedAt ? formatDate(investment.currentTermStartedAt) : '—'} />
                  <DataRow label="Interest (current term)" value={formatCurrency(investment.currentTermInterest)} />
                  <DataRow
                    label={`Interest (lifetime, across ${investment.rolloverCount + 1} terms)`}
                    value={formatCurrency(investment.totalInterest)}
                  />
                </>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Payment history" />
            {isLoadingPayments && <SkeletonTable columns={['Amount', 'Method', 'Status', 'Date']} rows={3} />}
            {!isLoadingPayments && payments.length === 0 && (
              <p className="py-6 text-center text-foreground-muted">No payments recorded yet.</p>
            )}
            {!isLoadingPayments && payments.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-foreground-subtle border-b border-border">
                      <th className="py-2 pr-4 font-medium">Amount</th>
                      <th className="py-2 pr-4 font-medium">Method</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td className="py-3 pr-4 text-foreground">{formatCurrency(p.amount)}</td>
                        <td className="py-3 pr-4 text-foreground-muted">{p.method || '—'}</td>
                        <td className="py-3 pr-4">
                          <Badge tone={p.status === 'success' ? 'success' : 'neutral'}>{(p.status || '—').toUpperCase()}</Badge>
                        </td>
                        <td className="py-3 text-foreground-muted">{formatDate(p.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <PayoutInterface summary={summary} />

          <Card>
            <CardHeader title="Payout history" />
            {isLoadingPayouts && <SkeletonTable columns={['Date', 'Type', 'Amount', 'Note']} rows={3} />}
            {!isLoadingPayouts && <PayoutTimeline payouts={payouts} />}
          </Card>

          {investment.rollover && <RolloverStatusCard rollover={investment.rollover} />}

          {investment.rollover && (
            <Card>
              <CardHeader title="Rollover history" />
              {isLoadingRollovers && <SkeletonTable columns={['Term', 'State', 'Date']} rows={2} />}
              {!isLoadingRollovers && <RolloverHistory rollovers={rollovers} />}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
